function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

import { useDisclosure, useEventListener, useId } from "@chakra-ui/hooks";
import { usePopper } from "@chakra-ui/popper";
import { callAllHandlers, mergeRefs } from "@chakra-ui/utils";
import { useCallback, useRef } from "react";
export function useTooltip(props) {
  if (props === void 0) {
    props = {};
  }

  var _props = props,
      _props$openDelay = _props.openDelay,
      openDelay = _props$openDelay === void 0 ? 0 : _props$openDelay,
      _props$closeDelay = _props.closeDelay,
      closeDelay = _props$closeDelay === void 0 ? 0 : _props$closeDelay,
      _props$closeOnClick = _props.closeOnClick,
      closeOnClick = _props$closeOnClick === void 0 ? true : _props$closeOnClick,
      closeOnMouseDown = _props.closeOnMouseDown,
      onOpen = _props.onOpen,
      onClose = _props.onClose,
      placement = _props.placement,
      id = _props.id,
      isOpenProp = _props.isOpen,
      defaultIsOpen = _props.defaultIsOpen,
      _props$arrowSize = _props.arrowSize,
      arrowSize = _props$arrowSize === void 0 ? 10 : _props$arrowSize,
      modifiers = _props.modifiers,
      isDisabled = _props.isDisabled;

  var _useDisclosure = useDisclosure({
    isOpen: isOpenProp,
    defaultIsOpen: defaultIsOpen,
    onOpen: onOpen,
    onClose: onClose
  }),
      isOpen = _useDisclosure.isOpen,
      onOpenProp = _useDisclosure.onOpen,
      onCloseProp = _useDisclosure.onClose;

  var popper = usePopper({
    forceUpdate: isOpen,
    placement: placement,
    arrowSize: arrowSize,
    modifiers: modifiers
  });
  var tooltipId = useId(id, "tooltip");
  var ref = useRef(null);
  var triggerRef = mergeRefs(ref, popper.reference.ref);
  var enterTimeout = useRef();
  var exitTimeout = useRef();
  var openWithDelay = useCallback(function () {
    if (!isDisabled) {
      enterTimeout.current = setTimeout(onOpenProp, openDelay);
    }
  }, [isDisabled, onOpenProp, openDelay]);
  var closeWithDelay = useCallback(function () {
    if (enterTimeout.current) {
      clearTimeout(enterTimeout.current);
    }

    exitTimeout.current = setTimeout(onCloseProp, closeDelay);
  }, [closeDelay, onCloseProp]);
  var onClick = useCallback(function () {
    if (closeOnClick) {
      closeWithDelay();
    }
  }, [closeOnClick, closeWithDelay]);
  var onMouseDown = useCallback(function () {
    if (closeOnMouseDown) {
      closeWithDelay();
    }
  }, [closeOnMouseDown, closeWithDelay]);

  var onKeyDown = function onKeyDown(event) {
    if (isOpen && event.key === "Escape") {
      closeWithDelay();
    }
  };

  useEventListener("keydown", onKeyDown);
  var getTriggerProps = useCallback(function (props, ref) {
    if (props === void 0) {
      props = {};
    }

    if (ref === void 0) {
      ref = null;
    }

    return _extends({}, props, {
      ref: mergeRefs(ref, triggerRef),
      onMouseLeave: callAllHandlers(props.onMouseLeave, closeWithDelay),
      onMouseEnter: callAllHandlers(props.onMouseEnter, openWithDelay),
      onClick: callAllHandlers(props.onClick, onClick),
      onMouseDown: callAllHandlers(props.onMouseDown, onMouseDown),
      onFocus: callAllHandlers(props.onFocus, openWithDelay),
      onBlur: callAllHandlers(props.onBlur, closeWithDelay),
      "aria-describedby": isOpen ? tooltipId : undefined
    });
  }, [closeWithDelay, isOpen, onClick, onMouseDown, openWithDelay, tooltipId, triggerRef]);
  var getTooltipProps = useCallback(function (props, ref) {
    if (props === void 0) {
      props = {};
    }

    if (ref === void 0) {
      ref = null;
    }

    return _extends({}, props, {
      id: tooltipId,
      role: "tooltip",
      ref: mergeRefs(ref, popper.popper.ref),
      style: _extends({}, props.style, popper.popper.style)
    });
  }, [popper.popper.ref, popper.popper.style, tooltipId]);
  var getArrowProps = useCallback(function (props, ref) {
    if (props === void 0) {
      props = {};
    }

    if (ref === void 0) {
      ref = null;
    }

    return _extends({}, props, {
      ref: mergeRefs(ref, popper.arrow.ref),
      style: _extends({}, props.style, popper.arrow.style)
    });
  }, [popper.arrow.ref, popper.arrow.style]);
  return {
    isOpen: isOpen,
    show: openWithDelay,
    hide: closeWithDelay,
    placement: popper.placement,
    getTriggerProps: getTriggerProps,
    getTooltipProps: getTooltipProps,
    getArrowProps: getArrowProps
  };
}